'use client'

import React, { useState } from 'react'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'
import { ArrowLeft, Globe, Settings } from 'lucide-react'
import { Brand } from '../../utils/mockData'
import { BrandSettings } from './BrandSettings'
import { BrandStrategyTab } from './BrandStrategyTab'
import { ColorPaletteGenerator } from './ColorPaletteGenerator'
import { TypographySystem } from './TypographySystem'
import { VoiceToneGuide } from './VoiceToneGuide'
import { LogoGuidelines } from './LogoGuidelines'
import { MoodBoardCreator } from './MoodBoardCreator'
import { BrandPositioning } from './BrandPositioning'
import { SWOTAnalysis } from './SWOTAnalysis'

// New optimized components
import { ProgressOverview } from './ProgressOverview'
import { TabNavigation, TabId } from './TabNavigation'
import { OverviewTab } from './OverviewTab'
import { ToolboxGrid } from './ToolboxGrid'
import { VisualIdentityTab } from './VisualIdentityTab'
import { BrandMessagingTab } from './BrandMessagingTab'
import { AIAssetsTab } from './AIAssetsTab'
import { ToolboxItem } from './brandDashboardData'
import { AIInsightsDashboard } from '../ai/AIInsightsDashboard'

interface BrandDashboardProps {
  brand: Brand
  onBack: () => void
}

export function BrandDashboard({ brand, onBack }: BrandDashboardProps) {
  const [selectedTab, setSelectedTab] = useState<TabId | 'settings'>('overview')
  const [selectedToolboxItem, setSelectedToolboxItem] = useState<string | null>(null)
  const [isProgressExpanded, setIsProgressExpanded] = useState(false)

  const handleToolboxItemClick = (item: ToolboxItem) => {
    setSelectedToolboxItem(item.id)
    setSelectedTab('toolbox')
  }

  const handleToolboxItemSelect = (itemId: string) => {
    setSelectedToolboxItem(itemId)
    setSelectedTab('toolbox')
  }

  const handleBackToToolbox = () => {
    setSelectedToolboxItem(null)
  }

  const handleOpenSettings = () => {
    setSelectedTab('settings')
  }

  const handleGenerateAssets = () => {
    console.log('Generate AI assets for:', brand.name)
    // TODO: Open AI asset generation modal
  }

  const handleInviteTeam = () => {
    setSelectedTab('settings')
  }

  const handlePublishBrand = () => {
    console.log('Publish brand page for:', brand.name)
    // TODO: Handle brand publishing
  }

  const handleUpgradeToPro = () => {
    console.log('Upgrade to Pro')
    // TODO: Navigate to pricing page
  }

  // Navigation handlers for AI Insights actions
  const handleNavigateToColorPalette = () => {
    setSelectedToolboxItem('color-palette')
    setSelectedTab('toolbox')
  }

  const handleNavigateToVoiceGuidelines = () => {
    setSelectedToolboxItem('voice-tone')
    setSelectedTab('toolbox')
  }

  const handleNavigateToAssetGeneration = () => {
    setSelectedTab('ai-assets')
  }

  // If settings tab is selected, render the settings component
  if (selectedTab === 'settings') {
    return <BrandSettings brand={brand} onBack={() => setSelectedTab('overview')} />
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm" onClick={onBack}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              <div className="h-6 w-px bg-slate-300" />
              <div className="flex items-center space-x-3">
                <Avatar className="h-12 w-12">
                  <AvatarImage src="" alt={brand.name} />
                  <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-500 text-white font-semibold">
                    {brand.name.substring(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-xl font-bold">{brand.name}</h1>
                    {brand.is_public && (
                      <Badge variant="secondary" className="bg-green-100 text-green-700">
                        <Globe className="w-3 h-3 mr-1" />
                        Published
                      </Badge>
                    )}
                  </div>
                  {brand.metadata?.industry && (
                    <p className="text-xs text-slate-500">{brand.metadata.industry}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Button variant="outline" size="sm" onClick={handleOpenSettings}>
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Button>
              <Button 
                size="sm" 
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                onClick={handlePublishBrand}
              >
                <Globe className="w-4 h-4 mr-2" />
                {brand.is_public ? 'Update' : 'Publish'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Progress Overview */}
        <ProgressOverview 
          brand={brand}
          isExpanded={isProgressExpanded}
          onToggleExpanded={() => setIsProgressExpanded(!isProgressExpanded)}
        />

        {/* Navigation Tabs */}
        <TabNavigation 
          selectedTab={selectedTab as TabId}
          onTabChange={setSelectedTab}
        />

        {/* Tab Content */}
        {selectedTab === 'overview' && (
          <div className="space-y-8">
            <OverviewTab 
              onGenerateAssets={handleGenerateAssets}
              onInviteTeam={handleInviteTeam}
              onUpgradeToPro={handleUpgradeToPro}
            />
            <AIInsightsDashboard 
              brand={brand} 
              onNavigateToColorPalette={handleNavigateToColorPalette}
              onNavigateToVoiceGuidelines={handleNavigateToVoiceGuidelines}
              onNavigateToAssetGeneration={handleNavigateToAssetGeneration}
            />
          </div>
        )}

        {selectedTab === 'toolbox' && !selectedToolboxItem && (
          <ToolboxGrid onItemClick={handleToolboxItemClick} />
        )}

        {selectedTab === 'toolbox' && selectedToolboxItem && (
          <div>
            <div className="mb-6">
              <Button variant="ghost" onClick={handleBackToToolbox} className="mb-4">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Toolbox
              </Button>
            </div>
            
            {/* Render the selected toolbox item */}
            {selectedToolboxItem === 'brand-strategy' && (
              <BrandStrategyTab brand={brand} />
            )}
            {selectedToolboxItem === 'color-palette' && (
              <ColorPaletteGenerator brand={brand} />
            )}
            {selectedToolboxItem === 'typography' && (
              <TypographySystem brand={brand} />
            )}
            {selectedToolboxItem === 'voice-tone' && (
              <VoiceToneGuide brand={brand} />
            )}
            {selectedToolboxItem === 'logo-guidelines' && (
              <LogoGuidelines brand={brand} />
            )}
            {selectedToolboxItem === 'mood-board' && (
              <MoodBoardCreator brand={brand} />
            )}
            {selectedToolboxItem === 'brand-positioning' && (
              <BrandPositioning brand={brand} />
            )}
            {selectedToolboxItem === 'swot-analysis' && (
              <SWOTAnalysis brand={brand} />
            )}
          </div>
        )}

        {selectedTab === 'brand-strategy' && (
          <BrandStrategyTab brand={brand} />
        )}

        {selectedTab === 'visual-identity' && (
          <VisualIdentityTab onToolboxItemSelect={handleToolboxItemSelect} />
        )}

        {selectedTab === 'brand-messaging' && (
          <BrandMessagingTab onToolboxItemSelect={handleToolboxItemSelect} />
        )}

        {selectedTab === 'ai-assets' && (
          <AIAssetsTab brand={brand} onGenerateAssets={handleGenerateAssets} />
        )}
      </div>
    </div>
  )
}